import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from '../../routing';
import { commandCenterApi, formatCurrency } from '../../utils/api';
import { useIsMobile } from '../../utils/useIsMobile';
import { formatDateShort, formatUploadedFull } from '../../utils/dateFormat';
import Icon from '../../components/Icon';
import StatInfo from '../../components/StatInfo';
import CommandCenterSearch from './CommandCenterSearch';
import ListRow from '../../components/ListRow';
import { AgeChip, LoadState } from './parts';
import BusinessSnapshot from './BusinessSnapshot';
import DuplicateDocsPanel from './DuplicateDocsPanel';

// מבט על ("Overview") — the console's home. Its job is to answer "what needs me right now?"
// before you read anything, and to route you there.
//
// Layout follows that: a headline that states the answer in a sentence, three
// WORK tiles for the queues you act on, then a quieter strip of STATE tiles you
// only look at when something is off. Six equal cards gave a zero the same
// visual weight as a seven, which made the screen a reading exercise rather
// than a glance.
//
// Still no primary button. The overview routes; it doesn't act.

/**
 * The distribution of a queue across the product's existing age bands
 * (>7 alert, 3–7 warn, <3 neutral — the vocabulary dueDateStatus already
 * teaches every user).
 *
 * Twenty fresh drafts and twenty stale ones are the same number and completely
 * different problems; the total alone can't tell them apart. Every segment is a
 * real count of real rows — this bar is data, not decoration.
 */
// Age bands, in the vocabulary the product already uses for overdue payments.
// Oldest first — in RTL the first segment lands on the right.
const BANDS = [
  { key: 'hot',   label: 'מעל 7 ימים' },
  { key: 'warm',  label: '3–7 ימים' },
  { key: 'fresh', label: 'עד 3 ימים' },
];

/**
 * The shape of a queue, as one bar with the counts written INSIDE it.
 *
 * Previously this was a 7px bar plus a full text line of key per tile — and
 * with three tiles side by side that key was printed three times to say the
 * same thing. The counts moved onto the segments and the key was lifted out to
 * a single shared line (see AgeKey), which is most of the height back.
 *
 * A number is only drawn on a segment wide enough to hold it (≥18% of the bar);
 * narrower bands stay pure colour rather than showing a clipped digit. Hover
 * still names every band and its count.
 */
function AgeBar({ buckets, total }) {
  if (!total) return null;
  const seg = BANDS.map(b => ({ ...b, n: buckets[b.key] })).filter(b => b.n > 0);
  return (
    <div className="cc-agebar" role="img"
      aria-label={seg.map(b => `${b.n} ${b.label}`).join(', ')}>
      {seg.map(b => (
        <span key={b.key} className={`cc-agebar-seg is-${b.key}`}
          style={{ flexGrow: b.n }} title={`${b.n} · ${b.label}`}>
          {b.n / total >= 0.18 ? b.n : ''}
        </span>
      ))}
    </div>
  );
}

/** One key for the whole row — the three tiles share the same colour scale. */
function AgeKey() {
  return (
    <div className="cc-agekey">
      {BANDS.map(b => (
        <span key={b.key} className="cc-agekey-item">
          <i className={`cc-agebar-dot is-${b.key}`} /> {b.label}
        </span>
      ))}
    </div>
  );
}

/**
 * A queue you work through. Big number, shape of the backlog, who to call.
 *
 * `help`, when given, renders a StatInfo note next to the label explaining
 * how the tile's number is worked out — StatInfo's trigger is a real
 * `<button>` (mobile needs a tap target, not a hover), so the tile itself is
 * a `role="button"` div rather than a `<button>`: nesting a button inside a
 * button is invalid HTML and browsers silently mangle it. A `role="button"`
 * div gets no native keyboard activation the way a real button does, so
 * onKeyDown replays it manually for Enter/Space — otherwise a keyboard user
 * can Tab to the tile, see it focused, and have no way to open it.
 */
function WorkTile({ label, block, to, cleanCopy, help }) {
  const navigate = useNavigate();
  const empty = block.total === 0;
  const activate = () => navigate(to);
  return (
    <div className={`cc-tile cc-tile--work${empty ? ' is-clean' : ''}`}
      role="button" tabIndex={0} onClick={activate}
      onKeyDown={(e) => {
        // keydown bubbles like any other event: if the StatInfo trigger
        // (a real, separately-focusable button) is what's actually focused,
        // this fires too — target !== currentTarget catches that case and
        // leaves it to the button's own native Enter/Space→click handling
        // (which itself stops the click from reaching here), so opening the
        // note via keyboard can never also navigate the tile away.
        if (e.target !== e.currentTarget) return;
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();   // Space must not also scroll the page
        activate();
      }}>
      <div className="cc-tile-label">
        {label}
        {help && <StatInfo label={label}>{help}</StatInfo>}
      </div>
      {empty ? (
        <div className="cc-tile-clean">
          <Icon name="check" size={15} /> {cleanCopy}
        </div>
      ) : (
        <>
          <div className="cc-tile-figure">
            <span className="cc-tile-value">{block.total}</span>
          </div>
          {/* The wider comparison count, in the vocabulary of stat-sub — a
              smaller, muted line under the headline number, not a second tile. */}
          {block.total_last_year != null && (
            <div className="cc-tile-subvalue">{block.total_last_year} בשנה האחרונה</div>
          )}
          {/* No "oldest is at X" line here: the headline above already names
              the single worst, and the by-customer table below lists every
              customer with its own age. Three places for one fact was what
              made these cards tall. */}
          <AgeBar buckets={block.age_buckets} total={block.total} />
        </>
      )}
    </div>
  );
}

/** A state you check, not a queue you work. Compact by design. */
// `to` navigates; `onClick` acts on this page instead (the duplicates tile
// opens the card at the bottom of the overview).
function StateTile({ label, value, tone, detail, to, onClick, clean }) {
  const navigate = useNavigate();
  const isClean = !value;
  const act = onClick || (to ? () => navigate(to) : undefined);
  return (
    <button type="button" className={`cc-state${isClean ? ' is-clean' : ''}`}
      onClick={act} disabled={!act}>
      <span className={`cc-state-value${tone && !isClean ? ` is-${tone}` : ''}`}>{value}</span>
      <span className="cc-state-body">
        <span className="cc-state-label">{label}</span>
        <span className="cc-state-detail">{isClean ? clean : detail}</span>
      </span>
    </button>
  );
}

/**
 * Rolls the three queues up BY CUSTOMER.
 *
 * The tiles answer "how much of each kind of work is there"; this answers "whose
 * work is it", which is the question that actually starts a phone call. Built
 * entirely from by_restaurant rows already in the overview payload — no extra
 * request, and no number here that isn't also in a tile.
 */
function byCustomer(data) {
  const rows = new Map();
  const add = (key, list) => {
    for (const r of list) {
      const id = r.restaurant_id ?? `x-${r.restaurant_name}`;
      if (!rows.has(id)) {
        rows.set(id, { id: r.restaurant_id, name: r.restaurant_name, drafts: 0, no_supplier: 0, invalid: 0, oldest: 0 });
      }
      const row = rows.get(id);
      row[key] = r.count;
      row.oldest = Math.max(row.oldest, r.oldest_age_days ?? 0);
    }
  };
  add('drafts', data.drafts.by_restaurant);
  add('no_supplier', data.no_supplier.by_restaurant);
  add('invalid', data.invalid.by_restaurant);
  // Oldest first — the same rule every queue in this console sorts by.
  return [...rows.values()].sort((a, b) => b.oldest - a.oldest);
}

/**
 * One sentence naming the most urgent thing, so the answer arrives before any
 * scanning does. Picks the queue with the oldest item — age is what turns a
 * backlog into a priority, and it's the same rule the tiles rank by.
 */
function headline(data) {
  const queues = [
    { key: 'drafts',      label: 'טיוטות ממתינות',   block: data.drafts,      to: '/command-center/queues' },
    { key: 'no_supplier', label: 'טיוטות ללא ספק',   block: data.no_supplier, to: '/command-center/queues?view=no-supplier' },
    { key: 'invalid',     label: 'מסמכים לא תקינים', block: data.invalid,     to: '/command-center/queues?view=invalid' },
  ].filter(q => q.block.total > 0);

  if (!queues.length) return null;
  queues.sort((a, b) => b.block.oldest_age_days - a.block.oldest_age_days);
  return queues[0];
}

/**
 * Email is down (#386). The only place that can say so: an alert by email would
 * go the way of every other email. It comes before the headline because nothing
 * else on this page is as urgent, and it clears itself on the next good send
 * (server/lib/mailHealth.js has the rule). A row of its own, not one button: an
 * invite has its own way back, on אנשים.
 */
function MailOutageLine({ outage }) {
  const navigate = useNavigate();
  if (!outage) return null;
  const invites = outage.by_kind?.invite || 0;
  return (
    <div className="cc-error cc-mail-outage" role="alert">
      <Icon name="alert" size={17} />
      <span className="cc-mail-outage-text">
        <b>המיילים לא נשלחים</b> · מאז {formatUploadedFull(outage.since)} · {outage.failures} ניסיונות נכשלו
        {outage.error && <span className="cc-mail-outage-error"> · <bdi>{outage.error}</bdi></span>}
      </span>
      <span className="cc-mail-outage-actions">
        {invites > 0 && (
          <button type="button" className="btn btn-sm btn-secondary" onClick={() => navigate('/command-center/people?pending=1')}>
            {invites === 1 ? 'הזמנה אחת' : `${invites} הזמנות`}
          </button>
        )}
        <button type="button" className="btn btn-sm btn-secondary" onClick={() => navigate('/command-center/queues?view=failed-sends')}>
          שליחות שנכשלו
        </button>
      </span>
    </div>
  );
}

export default function OverviewPage() {
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [duplicatesOpen, setDuplicatesOpen] = useState(false);
  const duplicatesRef = useRef(null);

  // The duplicates tile's target is the card at the foot of this page: open
  // it, then bring it into view once it has rendered open.
  const showDuplicates = () => {
    setDuplicatesOpen(true);
    requestAnimationFrame(() => duplicatesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  };

  const load = useCallback(async () => {
    setError(false);
    try {
      const r = await commandCenterApi.getOverview();
      setData(r.data);
    } catch {
      // A failed fetch must never render as a zero — "0 drafts" would read as
      // "nothing to do" and send an ops person away from a real backlog.
      setError(true);
      setData(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Refetch when the tab regains focus rather than polling: better-sqlite3 is
  // synchronous and single-process, and this is the pattern the drafts badge
  // already uses in the customer navbar.
  useEffect(() => {
    const onFocus = () => { if (!document.hidden) load(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [load]);

  const lead = data && headline(data);
  const customers = data ? byCustomer(data) : [];
  const failedSends = (data?.mail?.failed_forwards || 0) + (data?.mail?.failed_comments || 0);

  return (
    <>
      {/* Compact by intent. This was 21% of the viewport for a field plus three
          chips that duplicated the tiles directly beneath them; the chips are
          gone and the ⌘K hint moved inside the field. */}
      <div className="cc-hero">
        <CommandCenterSearch hero autoFocus={!isMobile} initialQuery={params.get('q') || ''} />
      </div>

      <LoadState loading={!data && !error} error={error} onRetry={load} empty={false}>
        {data && (
          <>
            <MailOutageLine outage={data.mail?.outage} />
            {lead ? (
              <button type="button" className="cc-headline" onClick={() => navigate(lead.to)}>
                <span className="cc-headline-dot" />
                <span className="cc-headline-text">
                  <b>{lead.block.total}</b> {lead.label} · הישן ביותר{' '}
                  <b>{lead.block.oldest_age_days === 0 ? 'היום' : `לפני ${lead.block.oldest_age_days} ימים`}</b>
                  {lead.block.worst && <> ב{lead.block.worst.restaurant_name}</>}
                </span>
                <Icon name="chevron-inline" size={15} className="cc-headline-go" />
              </button>
            ) : (
              // Worth its own state: "nothing needs you" is an answer, and six
              // zeroes is not a legible way to say it.
              <div className="cc-headline is-clean">
                <Icon name="check" size={17} />
                <span className="cc-headline-text">הכל נקי — אין פריטים שדורשים טיפול</span>
              </div>
            )}

            <div className="cc-worktiles">
              <WorkTile
                label="טיוטות ממתינות"
                block={data.drafts}
                to="/command-center/queues"
                cleanCopy="אין טיוטות ממתינות"
              />
              {/* The highest-value number in the product: bulk approve skips
                  drafts with no supplier, so they can never clear themselves
                  and nothing surfaced them before this console. */}
              <WorkTile
                label="טיוטות ללא ספק"
                block={data.no_supplier}
                to="/command-center/queues?view=no-supplier"
                cleanCopy="אין טיוטות תקועות"
              />
              <WorkTile
                label={`מסמכים לא תקינים - ${data.invalid.window_days} ימים אחרונים`}
                block={data.invalid}
                to="/command-center/queues?view=invalid"
                cleanCopy="אין מסמכים לא תקינים"
                help={(
                  <>
                    סופר מסמכים בסטטוס &quot;לא תקין&quot; שנוצרו ב-{data.invalid.window_days} הימים האחרונים
                    {' '}(מ-{formatDateShort(new Date(`${data.invalid.window_from}T00:00:00`))} עד {formatDateShort(new Date(`${data.invalid.window_to}T00:00:00`))}),
                    {' '}בכל המסעדות (לא כולל דמו).
                    <p className="stat-info-note">
                      המספר הקטן שמתחת הוא הסכום הכולל בשנה האחרונה, להשוואה.
                    </p>
                  </>
                )}
              />
            </div>
            <AgeKey />

            <BusinessSnapshot />

            <div className="cc-states">
              <StateTile
                label="רישום משתמשים בהמתנה"
                value={data.invites.total}
                tone="warn"
                detail={data.invites.items[0]?.email}
                clean="כולם נכנסו"
                to="/command-center/people?pending=1"
              />
              <StateTile
                label="לקוחות ללא הגדרות"
                value={data.config_gaps.total}
                tone="warn"
                detail={[
                  data.config_gaps.missing_business_id ? `${data.config_gaps.missing_business_id} חסר ח.פ.` : null,
                  data.config_gaps.missing_dokka ? `${data.config_gaps.missing_dokka} ללא דוקה` : null,
                ].filter(Boolean).join(' · ')}
                clean="כולם מוגדרים"
                to="/command-center/customers"
              />
              {/* This number gates a real safety property: the protective unique
                  index can only be created while zero duplicates exist. */}
              <StateTile
                label="כפילויות מספרי מסמך"
                value={data.data_health.duplicate_groups}
                tone="alert"
                detail="דורש ניקוי לפני הפעלת ההגנה"
                clean={data.data_health.index_active ? 'מוגן' : 'אין כפילויות · ההגנה לא פעילה'}
                onClick={showDuplicates}
              />
              {/* Every unlinked note is a supplier balance reading high in some
                  tenant. The tile routes to the לטיפול tab that lists them one
                  by one, each carrying the מרכזת it should be linked under —
                  the actual linking still happens on that invoice's document
                  view, opened cross-tenant like every other queue row. */}
              <StateTile
                label="תעודות משלוח לא מקושרות"
                value={data.unlinked_notes.note_count}
                tone="warn"
                detail={data.unlinked_notes.note_count
                  ? `${formatCurrency(data.unlinked_notes.total_amount)} · ${data.unlinked_notes.restaurant_count} לקוחות`
                  : ''}
                clean="הכל מקושר"
                to="/command-center/queues?view=unlinked"
              />
              {/* A document here is one the bookkeeper never got, whichever
                  customer's it is, and nothing else will send it. */}
              <StateTile
                label="שליחות שנכשלו"
                value={failedSends}
                tone="alert"
                detail={[
                  data.mail?.failed_forwards ? `${data.mail.failed_forwards} להנה"ח` : null,
                  data.mail?.failed_comments ? `${data.mail.failed_comments} הערות` : null,
                ].filter(Boolean).join(' · ')}
                clean="הכל נשלח"
                to="/command-center/queues?view=failed-sends"
              />
            </div>

            {customers.length > 0 && (
              <div className="cc-card cc-bycustomer">
                <div className="cc-card-head">
                  <h2 className="cc-card-title">לקוחות שדורשים טיפול</h2>
                  <span className="cc-muted" style={{ fontSize: '0.79rem' }}>לפי גיל הפריט הישן ביותר</span>
                </div>
                {isMobile ? (
                  // Six columns don't fit 375px; the rest of the console uses
                  // the shared two-line row at this width, so this does too.
                  <div className="m-list">
                    {customers.map(c => (
                      <ListRow
                        key={c.id ?? c.name}
                        primary={c.name || '—'}
                        meta={
                          <>
                            <AgeChip days={c.oldest} />
                            <span>{c.drafts} טיוטות</span>
                            {c.no_supplier > 0 && <span style={{ color: '#b91c1c', fontWeight: 700 }}>· {c.no_supplier} ללא ספק</span>}
                            {c.invalid > 0 && <span>· {c.invalid} לא תקינים</span>}
                          </>
                        }
                        onClick={() => navigate(`/command-center/customers/${c.id}`)}
                      />
                    ))}
                  </div>
                ) : (
                <div className="table-wrapper">
                  <table>
                    <thead>
                      <tr>
                        <th>מסעדה</th>
                        <th>הישן ביותר</th>
                        <th>טיוטות</th>
                        <th>ללא ספק</th>
                        <th>לא תקינים</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {customers.map(c => (
                        <tr key={c.id ?? c.name} className="cc-row-link"
                          onClick={() => navigate(`/command-center/customers/${c.id}`)}>
                          <td style={{ fontWeight: 600 }}>{c.name || '—'}</td>
                          <td><AgeChip days={c.oldest} /></td>
                          <td className="cc-num">{c.drafts || <span className="cc-muted">—</span>}</td>
                          <td className="cc-num">
                            {c.no_supplier
                              ? <span style={{ color: '#b91c1c', fontWeight: 700 }}>{c.no_supplier}</span>
                              : <span className="cc-muted">—</span>}
                          </td>
                          <td className="cc-num">{c.invalid || <span className="cc-muted">—</span>}</td>
                          <td className="cc-go"><Icon name="chevron-inline" size={14} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                )}
              </div>
            )}

            {/* Last on the page and closed by default: a maintenance check you
                open when the tile above says to, not daily work. */}
            <DuplicateDocsPanel
              ref={duplicatesRef}
              open={duplicatesOpen}
              onToggle={() => setDuplicatesOpen(o => !o)}
              indexActive={data.data_health.index_active}
              groupCount={data.data_health.duplicate_groups}
            />
          </>
        )}
      </LoadState>
    </>
  );
}
